import { useField } from 'formik';
import { isString } from 'lodash-es';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import InputMask from 'react-input-mask';
import { useDebounce } from 'react-use';

import * as constants from 'utils/constants';

import * as helpers from './helpers';
import { ComponentProps } from './interfaces';
import * as Styled from './styled';

export const Input = React.forwardRef<HTMLInputElement, ComponentProps>(function Input(
  {
    delay = constants.INPUT_DELAY,
    name,
    label = '',
    ariaLabel = '',
    type = 'text',
    disabled = false,
    placeholder = '',
    as = 'Normal',
    maskingOptions,
    linkText,
    hint,
    maxLength = 100,
    children,
    labelProps,
    errorLabelProps,
    ...props
  },
  ref
) {
  const [field, meta, fieldHelpers] = useField(name);
  const [innerValue, setInnerValue] = useState('');
  const changeEventRef = useRef<React.ChangeEvent<HTMLInputElement>>();

  /**
   * Displaying a user error message
   */
  const showError = meta.touched && meta.error;

  /**
   * Input Mask masking template
   */
  let mask: string | Array<string | RegExp> | undefined;
  /**
   * Symbol converting object for the Input Mask
   */
  let formatChars: { [key: string]: string } | undefined;
  /**
   * Get the value of the field
   */
  const { value, onBlur } = field;

  // Add a "blur" event that will automatically trim all whitespaces from left & right sides of the string followed by triggering the original "field" onBlur
  const handleOnBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      e.preventDefault();
      if (isString(value)) {
        fieldHelpers.setValue(value.trim());
      }
      onBlur(e);
    },
    [value, fieldHelpers, onBlur]
  );

  /**
   * Using a debounce efect for the input to avoid having the field calling handleChange on every key stroke.
   * ref:
   * https://medium.com/@vlad_19457/reducing-the-number-of-renders-when-using-formik-9790bf111ab9)
   * https://github.com/streamich/react-use/blob/master/docs/useDebounce.md
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isReady, cancel] = useDebounce(
    () => {
      if (changeEventRef.current) {
        field.onChange(changeEventRef.current);
      }
    },
    delay,
    [changeEventRef.current]
  );

  // Effect to update the input field internal value
  useEffect(() => {
    if (value) {
      setInnerValue(value as string);
    } else {
      setInnerValue('');
    }
  }, [value]);

  /**
   * Event handler when input value gets changed
   */
  const handleOnChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    event.persist();

    const newValue = event.currentTarget.value;
    setInnerValue(newValue);
    changeEventRef.current = event;
  }, []);

  // Calcuate the masking settings for the input
  if (maskingOptions) {
    mask = maskingOptions.mask;
    formatChars = maskingOptions.formatChars;
  } else {
    const options = helpers.getMasking(value, as);
    if (options) {
      mask = options.mask;
      formatChars = options.formatChars;
    }
  }
  /**
   * The way that the react-input-mask components sets the ref is through the old ref api (from React 15)
   * We can wrap the ref in the below so that modern refs still work.
   *
   * It would be good to be able to remove this at some point in the future.
   *
   * See source for why this is so janky:
   * https://github.com/sanniassin/react-input-mask/blob/e1b3a7ca1b8728e518a3f1dc3e9743916d73a272/src/index.js#L545
   */
  const setRef = React.useCallback(
    innerRef => {
      if (typeof ref === 'object' && ref !== null) {
        ref.current = innerRef;
      } else if (typeof ref === 'function') {
        ref(innerRef);
      }
    },
    [ref]
  );

  return (
    <Styled.Wrapper>
      <Styled.Label htmlFor={name} id={`${name}.label`} {...labelProps}>
        {label}
      </Styled.Label>
      <Styled.Input
        {...props}
        as={InputMask}
        inputRef={setRef}
        name={field.name}
        aria-label={ariaLabel || label}
        value={innerValue}
        onChange={handleOnChange}
        onBlur={handleOnBlur}
        type={type}
        disabled={disabled}
        placeholder={placeholder}
        maxLength={!mask ? maxLength : undefined}
        mask={mask}
        maskChar={''}
        formatChars={formatChars}
        aria-invalid={!!showError}
        aria-labelledby={`${name}.label`}
      />
      {children}
      {showError && <Styled.ErrorLabel {...errorLabelProps}>{meta.error}</Styled.ErrorLabel>}
      {hint && <Styled.HintLabel>{hint}</Styled.HintLabel>}
      {linkText && <Styled.LinkLabel as='button'>{linkText}</Styled.LinkLabel>}
    </Styled.Wrapper>
  );
});

export default Input;
